import axios from "axios";
import React from "react";
import Box from "@mui/material/Box";

import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import NoWrapTooltip from "../UtilityComponents/TooltipUtilityComponents/NoWrapTooltip";

import AddIcon from "@mui/icons-material/Add";

import ScenarioIntro from "./ScenarioIntro";
import ScenarioDisplayProps from "./ScenarioDisplayProps";
import { ToggleButton } from "@mui/material";
import PeriodToggleGroup from "../UtilityComponents/GraphOptionUtilityComponents/PeriodToggleGroup";
import periodToXAxisRange from "../UtilityComponents/GraphOptionUtilityComponents/periodToXAxisRange";
import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";

import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";

import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";

import { useTheme } from "@mui/material/styles";
import { getFuturePeriodStats } from "./getFuturePeriodStats";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const summaryStatPaddingTopBottom = 1;
const enterToolTipDelay = 400;
const leftPadding = 4;

function ScenarioSimpleDisplay(props: ScenarioDisplayProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentStats = getFuturePeriodStats(props);

  return (
    <Box
      sx={{
        overflowY: props.noneSelected ? "auto" : undefined,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {props.noneSelected ? (
        <Box
          sx={{
            flexGrow: 0,
            py: summaryStatPaddingTopBottom,
            px: 2,
            sizing: "border-box",
          }}
        >
          <Typography
            variant="h4"
            color="info.main"
            sx={{ position: "sticky", top: 0, background: theme.palette.background.paper }}
          >
            Explore forecast options with historical data
          </Typography>
          <Typography variant="h5" color="text.secondary">
            <LearnTextEmbed content="Learn about some forecasts for your future stock expectations." />
            <br />
            Click any of the <AddIcon />
            <LearnTextEmbed
              content=" to see how your vesting and stock could grow in the future. Our proprietary technology will even model the trades under your strategy if you have one selected!
            Click added forecasts to compare charts to help you pick a forecast, strategy, and settings for the future. We'll do the rest!"
            />
          </Typography>
        </Box>
      ) : null}

      {props.noneSelected || !props.plotTitle ? null : (
        <>
          <Box sx={{ display: "flex", flexDirection: "row", overflowX: "hidden", justifyContent: "flex-start", ml: 2 }}>
            <Tooltip
              title={"Future Portfolio Value with " + props.plotTitle.trim()}
              placement="top"
              arrow
              enterDelay={enterToolTipDelay}
            >
              <Typography variant="h4" color="text" sx={{ whiteSpace: "nowrap" }}>
                Future Portfolio Value with
                <Typography
                  variant="inherit"
                  color="info.main"
                  sx={{ fontWeight: 500, whiteSpace: "nowrap", display: "inline" }}
                >
                  {" " + props.plotTitle.trim()}
                </Typography>
              </Typography>
            </Tooltip>
            {props.plotExtraInfo ? (
              <Box sx={{ alignSelf: "center", pl: 0.5 }}>
                <NoWrapTooltip
                  title={<div style={{ whiteSpace: "pre-line" }}>{props.plotExtraInfo}</div>}
                  placement="bottom"
                  arrow
                >
                  <InfoOutlinedIcon color="info" />
                </NoWrapTooltip>
              </Box>
            ) : null}
          </Box>
          {props.plotSubtitle ? (
            <Box
              sx={{ display: "flex", flexDirection: "row", overflowX: "hidden", justifyContent: "flex-start", ml: 2 }}
            >
              <Tooltip
                title={"Future Trades for your Selected " + props.plotSubtitle.trim() + " Strategy"}
                placement="top"
                arrow
                enterDelay={enterToolTipDelay}
              >
                <Typography variant="h5" color="text" sx={{ whiteSpace: "nowrap" }}>
                  Future Trades based on
                  <Typography
                    variant="inherit"
                    color="info.main"
                    sx={{ fontWeight: 500, whiteSpace: "nowrap", display: "inline" }}
                  >
                    {" " + props.plotSubtitle.trim()}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigate("/strategies?edit=locked_strategy")}
                    sx={{ alignSelf: "center" }}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Typography>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "row", overflowX: "hidden", justifyContent: "center" }}>
              <Tooltip
                title="Add Future Trades to Projections Here by Selecting a Strategy"
                placement="top"
                arrow
                enterDelay={enterToolTipDelay}
              >
                <Typography variant="h5" color="text" sx={{ whiteSpace: "nowrap" }}>
                  Add Future Trades to Projections Here by{" "}
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/strategies")}
                    sx={{ alignSelf: "center" }}
                  >
                    Selecting a Strategy
                  </Button>
                </Typography>
              </Tooltip>
            </Box>
          )}
        </>
      )}

      {props.noneSelected ? (
        props.scenarioOptions && (
          <ScenarioIntro
            scenarioOptions={props.scenarioOptions}
            spinAddScenario={props.spinAddScenario}
            handleAdd={props.handleAdd}
          />
        )
      ) : (
        <Box
          sx={{
            // height: percentOfPaperHeightForSummaryStats, // TODO: set a min height
            flexDirection: "column",
            py: summaryStatPaddingTopBottom,
            pl: leftPadding,
            display: "flex",
            sizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            overflowX: "auto",
          }}
        >
          <AsOf referenceDateString={currentStats.futureReferenceDateString + " " + props.referenceDateString} />
          {[
            {
              description: "in total value",
              value: currentStats.totalValue,
              lastDrawn: props.lastDrawnTotalValue,
              setLastDrawn: props.setLastDrawnTotalValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the total value under the displayed forecast.",
              display: true,
            },
            {
              description: "in " + props.symbol + " stock value",
              value: currentStats.coreStockValue,
              lastDrawn: props.lastDrawnCoreStockValue,
              setLastDrawn: props.setLastDrawnCoreStockValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the value of " + props.symbol + " stock under the displayed forecast.",
              display: true,
            },
            {
              description: "in " + props.otherSymbol ? props.otherSymbol + " stock value" : "other stock value",
              value: currentStats.otherStockValue,
              lastDrawn: props.lastDrawnOtherStockValue,
              setLastDrawn: props.setLastDrawnOtherStockValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the value of other stock under the displayed forecast.",
              display: true, // currentStats.otherStockValue > 0,
            },
            {
              description: "in cash value",
              value: currentStats.cashValue < 0 && currentStats.cashValue > -0.1 ? 0 : currentStats.cashValue, // if negative ten cents make it zero for some rounding error
              lastDrawn: props.lastDrawnCashValue,
              setLastDrawn: props.setLastDrawnCashValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the cash accumulated under the displayed forecast.",
              display: true,
            },
            // {
            //   description: "Sharpe Ratio",
            //   value: props.sharpeRatio,
            //   decimals: 2,
            //   prepend: "",
            //   tooltip_text: "This is a normalized metric for risk adjusted returns.",
            // },
          ].map(
            (label, index) =>
              label.display && (
                <Tooltip
                  title={label.tooltip_text}
                  placement="bottom-start"
                  arrow
                  key={label.description}
                  enterDelay={enterToolTipDelay}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      sizing: "border-box",
                      alignItems: "flex-end",
                      gap: 2,
                    }}
                  >
                    <Typography variant="h4" noWrap sx={{}}>
                      {props.noneSelected ? (
                        label.prepend + "-"
                      ) : props.updated ? (
                        <MyCountUp
                          value={label.value}
                          lastFullyDrawnValue={label.lastDrawn}
                          setLastFullyDrawnValue={label.setLastDrawn}
                          prefix={label.prepend}
                          suffix=""
                          decimals={label.decimals}
                          duration={1}
                          negativeIsError
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </Typography>
                    <Typography variant="h6" noWrap>
                      {label.description + " on " + currentStats.futureReferenceDateString.replace(" stats", "")}
                    </Typography>
                  </Box>
                </Tooltip>
              )
          )}
          <Box
            sx={{
              flexGrow: 0,
              sizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignSelf: "center",
            }}
          >
            <PeriodToggleGroup
              period={props.futurePeriod || "Max"}
              setPeriod={props.setFuturePeriod || ((newHistoricalPeriod) => null)}
              periodArray={["Y/E", "1 Yr", "2 Yr", "Max"]}
              isFuture
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
export default ScenarioSimpleDisplay;
